import {Country, isFeatureEnabled, User} from '@hconnect/apiclient'
import {Content, Typography} from '@hconnect/uikit'
import {Button, CardHeader, DashboardCard, PageHeader, Pill, TextRegular} from '@hm/fundamental'
import {Box} from '@material-ui/core'
import {mdiPencilOutline, mdiPlus} from '@mdi/js'
import {FormControlLabel, Switch} from '@mui/material'
import React, {useEffect, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useHistory, useLocation, useParams} from 'react-router-dom'

import {api} from '../../App.store'
import {useAdminPilotUser} from '../../common/hooks/useAdminPilotUser'
import {useCountriesConfiguration} from '../../common/hooks/useCountriesConfiguration'
import {useFeaturesCheckFetcher} from '../../common/hooks/useFeaturesCheckFetcher'
import {useRolesConfigurationForUserId} from '../../common/hooks/useRolesConfiguration'
import {useUser} from '../../common/hooks/useUser'
import {useUserCrossCountriesConfiguration} from '../../common/hooks/useUserCrossCountriesConfiguration'
import {useUserRoles} from '../../common/hooks/useUserRoles'
import {GeneralInformationForm} from '../../components/GeneralInformationForm/GeneralInformationForm'
import {TfaDialog} from '../../components/UserInformation/dialogs/TfaDialog'
import {UserInformationFormData} from '../../components/UserInformation/UserInformationForm.types'
import {getLiveProducts} from '../../components/UserInformation/utils/utils'
import {useLocalStorage} from '../../hooks/useLocalStorage'
import {useLoggedInUserPermissions, useLoggedInUserProfile} from '../../hooks/useLoggedInUser'
import {useTranslation} from '../../hooks/useTranslation'
import {Permission} from '../../modules/LoggedInUser'
import {RoleAssignment} from '../../modules/ManageUsers.selectors'

import {InvitationProducts} from './components/InvitationProducts'
import {RolesFilters} from './components/RolesFilters'
import {TwoFactorAuthInformation} from './components/TwoFactorAuthInformation'
import {DeleteUserDialog} from './dialogs/DeleteUserDialog'
import {InvitationDialog} from './dialogs/InvitationDialog'
import {ResetPasswordDialog} from './dialogs/ResetPasswordDialog'
import {ManageUserHeader} from './header/ManageUserHeader'
import {useDeleteUser} from './hooks/useDeleteUser'
import {useInvitationProducts} from './hooks/useInvitationProducts'
import {useResetPassword} from './hooks/useResetPassword'
import {useTfaData} from './hooks/useTfaData'
import {useUpdateUser} from './hooks/useUpdateUser'
import {InviteUserButtons} from './InviteUserButtons'
import {ManageUserIssues} from './issues/ManageUserIssues'
import {useStyles} from './ManageUser.styles'
import {ManageUserActions} from './ManageUserActions'
import {ManageUserRoles} from './roles/ManageUserRoles'
import {RolesViewContainer} from './RolesViewContainer'
import {handleAdminPilotRoles} from './utils/handleAdminPilotRoles'
import {handleUserData} from './utils/handleUserData'

export type ManageUserProps = {
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
}

// eslint-disable-next-line complexity
export const ManageUser = ({loggedInUserPermissions, loggedInUserProfile}: ManageUserProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const history = useHistory<{source: string}>()
  const {data: features, isLoading: isLoadingFeatures} = useFeaturesCheckFetcher(api)
  const [isAllCollapsed, setIsAllCollapsed] = useState(true)
  const [isEditing, setEditing] = useState(false)
  const [showAutoRoles, setShowAutoRoles] = useState(true)
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false)
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false)
  const [showInvitationDialog, setShowInvitationDialog] = useState(false)
  const [showNewRoleAssignmentForm, setShowNewRoleAssignmentForm] = useState(false)
  const [showTfaDialog, setShowTfaDialog] = useState(false)
  const {state} = useLocation()
  const {userId} = useParams<{userId?: string}>()
  const queryParams = new URLSearchParams(location.search)
  const loggedInUserProfileData = useLoggedInUserProfile(loggedInUserProfile)
  const loggedInUserPermissionsData = useLoggedInUserPermissions(loggedInUserPermissions)
  const {data: loggedInUserRoles} = useUserRoles(loggedInUserProfileData?.user_id)
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissionsData)

  const includeCrossBorderUser = queryParams.get('includeCrossBorderUser') === 'true'
  const shouldShowNewUserProfilePage = isFeatureEnabled(
    features || [],
    {countryId: loggedInUserProfileData?.country || ''},
    'ShowNewUserProfilePage'
  )

  const {
    data: roles,
    refetch: refetchRoles,
    isLoading: isRolesLoading,
    isError: isRolesError
  } = useUserRoles(userId)

  const adminPilotRoles: RoleAssignment[] = []

  if (isUserAdminPilot) {
    handleAdminPilotRoles(adminPilotRoles, loggedInUserRoles, roles)
  }

  const [showNewUserProfile, setShowNewUserProfile] = useLocalStorage('newUserProfile', true)
  const [filteredRoles, setFilteredRoles] = useState<RoleAssignment[] | null | undefined>(roles)

  useEffect(() => {
    if (isUserAdminPilot) {
      setFilteredRoles(adminPilotRoles)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPilotRoles])

  const {
    data: user,
    refetch: refetchUser,
    isLoading: isUserLoading,
    isError: isUserError
  } = useUser(userId, includeCrossBorderUser)

  const {
    data: userCountries,
    isLoading: isUserCountriesLoading,
    error: isUserCountriesError
  } = useCountriesConfiguration()

  const {roles: roleConfigurations} = useRolesConfigurationForUserId(loggedInUserProfile)

  const liveProducts = getLiveProducts(userCountries ?? null, user)

  const {filteredProducts} = useInvitationProducts(liveProducts, loggedInUserPermissions, roles)

  const {data: tfaData, isError: isTfaError} = useTfaData(userId)

  const {data: viewCrossCountries} = useUserCrossCountriesConfiguration(
    loggedInUserRoles,
    includeCrossBorderUser
  )

  const [availableCountries, setAvailableCountries] = useState<Country[]>()

  useEffect(() => {
    const allCountries = [
      ...(userCountries || []),
      ...(viewCrossCountries?.filter((x) => x.countryCode === user?.country) || [])
    ]
    const uniqueCountries = allCountries.reduce((acc, current) => {
      if (!acc.some((country) => country.countryCode === current.countryCode)) {
        acc.push(current)
      }
      return acc
    }, [] as Country[])
    setAvailableCountries(uniqueCountries)
  }, [userCountries, viewCrossCountries, user?.country])

  const userForm = useMemo(
    () => ({
      name: user?.name ?? '',
      eMail: user?.eMail ?? '',
      mobileNumber: user?.mobileNumber ?? '',
      companyName: user?.companyName ?? '',
      country: user?.country ?? '',
      availableCountries,
      defaultBranding: user?.defaultBranding ?? '',
      primaryOrganisationalUnit: user?.primaryOrganisationalUnit ?? '',
      marketId:
        (user?.country === 'CA' || user?.country === 'US') && !user?.marketId
          ? 'hproduce'
          : user?.marketId ?? '',
      defaultLocale: user?.defaultLocale ?? '',
      isTester: !!user?.isTester,
      twoFactorEnabled: user?.twoFactorEnabled,
      blocklistNotifications: user?.blocklistNotifications
    }),
    [user, availableCountries]
  )

  const formMethods = useForm<UserInformationFormData>({
    defaultValues: userForm,
    mode: 'all',
    reValidateMode: 'onChange'
  })

  const {handleUpdateUser} = useUpdateUser({
    setEditing,
    formMethods,
    refetchUser,
    userData: user,
    emailFieldName: 'eMail',
    mobileNumberFieldName: 'mobileNumber',
    loggedInUserProfile: loggedInUserProfileData
  })
  const {handleDeleteUser} = useDeleteUser({setShowDeleteUserDialog, userData: user})
  const {handleResetPassword} = useResetPassword({
    setShowResetPasswordDialog,
    userData: user,
    loggedInUserProfile: loggedInUserProfileData
  })

  useEffect(() => {
    userForm && formMethods.reset(userForm)
  }, [formMethods, userForm, availableCountries])

  useEffect(() => {
    if (isLoadingFeatures) {
      return
    }

    if (shouldShowNewUserProfilePage) {
      setShowNewUserProfile(
        showNewUserProfile === null ? shouldShowNewUserProfilePage : showNewUserProfile
      )
    } else {
      setShowNewUserProfile(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowNewUserProfilePage])

  const {accountNumber, extraInfo} = handleUserData(user)

  const isError = isUserError || isRolesError || isUserCountriesError

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        {t('error.notFound')}
      </Box>
    )
  }

  const canEditUserEmailPhone =
    loggedInUserProfileData?.id === userId ||
    loggedInUserPermissionsData.some(
      (permission) => permission.permissionType === 'CHANGE_USER_LOGIN'
    )

  const isAdAccount = tfaData?.status === 204
  return (
    <Content data-test-id="manageuser-page">
      <PageHeader
        data-cy="userslist-page"
        backToPageLabel="Back"
        className="force-header-white pl-0 pr-0 md:pl-0 md:pr-0"
        subtitle="User profile"
        backToPageUrlOnClick={() => (state ? history.goBack() : history.push('/'))}
        aside={
          <div className="flex">
            {shouldShowNewUserProfilePage && (
              <FormControlLabel
                data-test-id="toggle-new-user-profile"
                label={t('manageUser.newUserProfile')}
                control={
                  <Switch
                    onChange={(_, checked) => setShowNewUserProfile(checked)}
                    className="!mr-1"
                    checked={showNewUserProfile}
                  />
                }
              />
            )}
            {showNewUserProfile && (
              <ManageUserActions
                loggedInUserProfile={loggedInUserProfileData}
                loggedInUserPermissions={loggedInUserPermissions}
                user={user}
                isPilot={isUserAdminPilot}
              />
            )}
          </div>
        }
      >
        {
          <div className="flex gap-2 items-center">
            <span>{user?.name || t('manageUser.manageUser')}</span>
            {user?.isTester && (
              <Pill
                type="neutral"
                variant="solid"
                shape="square"
                text={t('manageUser.testUser')}
                data-cy="test-user-flag"
              />
            )}
          </div>
        }
      </PageHeader>

      {user && (
        <ManageUserIssues
          user={user}
          accountNumber={accountNumber}
          extraInfo={extraInfo}
          liveProducts={liveProducts}
          refetchUser={refetchUser}
          loggedInUserPermissions={loggedInUserPermissions}
          showNewUserProfile={showNewUserProfile}
        />
      )}
      {showNewUserProfile && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2.5fr] gap-4 mt-4 max-h-1500px mb-[52px]">
          <div className="flex flex-col gap-4">
            <DashboardCard>
              <CardHeader
                asideClassname="w-full md:w-auto"
                aside={
                  <div className="flex w-full md:w-auto">
                    {isEditing ? (
                      <div className="flex justify-end gap-2 basis-full shrink-0 grow-0 md:grow-0 md:basis-auto md:shrink-1">
                        <Button variant="secondary" onClick={() => setEditing(false)}>
                          {t('manageUser.discard')}
                        </Button>
                        <Button
                          data-cy="button-save-changes"
                          onClick={formMethods.handleSubmit(handleUpdateUser)}
                        >
                          {t('manageUser.save')}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        data-test-id="button-edit-userInfo"
                        variant="secondary"
                        onClick={() => setEditing(true)}
                        icon={mdiPencilOutline}
                      >
                        {t('manageUser.edit')}
                      </Button>
                    )}
                  </div>
                }
              >
                {t('manageUser.generalInformation')}
              </CardHeader>
              <GeneralInformationForm
                formMethods={formMethods}
                isLoading={isUserLoading || isUserCountriesLoading}
                isReadonly={!isEditing}
                onSubmit={handleUpdateUser}
                canEditUserEmailPhone={canEditUserEmailPhone}
                user={user}
                loggedInUserProfile={loggedInUserProfileData}
              />
            </DashboardCard>
            <DashboardCard>
              <CardHeader
                wrapAside={false}
                aside={
                  <Button
                    variant="secondary"
                    icon={mdiPencilOutline}
                    onClick={() => setShowInvitationDialog(true)}
                    data-cy="button-invite-user"
                    disabled={filteredProducts.every(
                      (product) =>
                        user?.invitationProducts
                          ?.map((invitationProduct) => invitationProduct.product)
                          .includes(product)
                    )}
                  >
                    {t('manageUser.edit')}
                  </Button>
                }
              >
                {t('manageUser.invitations')}
              </CardHeader>
              <InvitationProducts invitationProducts={user?.invitationProducts || []} />
            </DashboardCard>
            <DashboardCard>
              <CardHeader
                wrapAside={false}
                aside={
                  <Button
                    variant="secondary"
                    icon={mdiPencilOutline}
                    onClick={() => setShowTfaDialog(true)}
                    disabled={isAdAccount}
                    data-cy="button-add-security-method"
                  >
                    {t('manageUser.edit')}
                  </Button>
                }
              >
                {t('manageUser.twoFA')}
              </CardHeader>
              <TwoFactorAuthInformation tfaData={tfaData?.data} isAdAccount={isAdAccount} />
            </DashboardCard>
          </div>
          <DashboardCard>
            <CardHeader
              aside={
                roleConfigurations &&
                roleConfigurations.length > 0 && (
                  <Button
                    data-test-id="button-add-role"
                    variant="primary"
                    icon={mdiPlus}
                    onClick={() => setShowNewRoleAssignmentForm(true)}
                    type="button"
                  >
                    {t('manageUser.assignRoles')}
                  </Button>
                )
              }
            >
              {t('manageUser.roles')}
            </CardHeader>
            <div className="lg:mb-4 flex flex-col gap-4">
              <RolesFilters
                roles={roles || []}
                setFilteredRoles={setFilteredRoles}
                isAllCollapsed={isAllCollapsed}
                setIsAllCollapsed={setIsAllCollapsed}
              />
              {(roles?.length || 0) > 0 && filteredRoles?.length === 0 && (
                <TextRegular>{t('rolesFilters.noRolesFound')}</TextRegular>
              )}
            </div>
            <div className="max-h-800px min-h-500px overflow-y-auto">
              <RolesViewContainer
                roleAssignments={filteredRoles}
                showAutoGeneratedRoles={true}
                loggedInUserPermissions={loggedInUserPermissions}
                loggedInUserProfile={loggedInUserProfileData}
                refetchRoles={refetchRoles}
                showNewRoleAssignmentForm={showNewRoleAssignmentForm}
                setShowNewRoleAssignmentForm={setShowNewRoleAssignmentForm}
                isAllCollapsed={isAllCollapsed}
              />
            </div>
          </DashboardCard>
        </div>
      )}
      {!showNewUserProfile && (
        <div className="bg-white rounded-xl p-6">
          <ManageUserHeader
            formMethods={formMethods}
            handleUpdateUser={handleUpdateUser}
            isEditing={isEditing}
            setEditing={setEditing}
            setShowDeleteUserDialog={setShowDeleteUserDialog}
            setShowResetPasswordDialog={setShowResetPasswordDialog}
            isLoading={isUserLoading || isUserCountriesLoading}
            user={user}
            tfaData={tfaData?.data}
            isTfaError={isTfaError || tfaData?.status === 204}
            roles={roles}
            loggedInUserPermissions={loggedInUserPermissions}
            loggedInUserProfile={loggedInUserProfileData}
          />
          <ManageUserRoles
            showAutoRoles={showAutoRoles}
            setShowAutoRoles={setShowAutoRoles}
            accountNumber={accountNumber}
            extraInfo={extraInfo}
            refetchRoles={refetchRoles}
            userId={userId}
            userCountry={user?.country ?? ''}
            roles={roles}
            isLoading={isRolesLoading}
            loggedInUserPermissions={loggedInUserPermissions}
            loggedInUserProfile={loggedInUserProfileData}
          />
          <Box my={4}>
            <Typography variant="h2" gutterBottom className={classes.sectionHeader}>
              {t('manageUser.inviteUser')}
            </Typography>
            <InviteUserButtons
              data-test-id="invite-buttons-box"
              user={user}
              products={liveProducts}
              refetchUser={refetchUser}
              roles={roles || []}
              loggedInUserPermissions={loggedInUserPermissions}
            />
          </Box>
        </div>
      )}

      {user && showDeleteUserDialog && (
        <DeleteUserDialog
          showDeleteUserDialog={showDeleteUserDialog}
          setShowDeleteUserDialog={setShowDeleteUserDialog}
          handleDeleteUser={handleDeleteUser}
        />
      )}
      {user && (
        <ResetPasswordDialog
          showResetPasswordDialog={showResetPasswordDialog}
          setShowResetPasswordDialog={setShowResetPasswordDialog}
          handleResetPassword={handleResetPassword}
        />
      )}
      {showTfaDialog && (
        <TfaDialog
          setShowTfaDialog={setShowTfaDialog}
          showTfaDialog={showTfaDialog}
          tfaType={tfaData?.data.tfaType || 'none'}
          mobileNumber={formMethods.getValues().mobileNumber}
          edit={!!tfaData?.data.isTfaEnabled}
          loggedInUserProfile={loggedInUserProfileData}
        />
      )}
      {showInvitationDialog && (
        <InvitationDialog
          open={showInvitationDialog}
          setOpenModal={setShowInvitationDialog}
          products={liveProducts}
          data-test-id="invite-buttons-box"
          user={user}
          refetchUser={refetchUser}
          roles={roles || []}
          loggedInUserPermissions={loggedInUserPermissions}
          onClose={() => setShowInvitationDialog(false)}
          secondActionText={t('manageUser.close')}
          secondAction={() => setShowInvitationDialog(false)}
        />
      )}
    </Content>
  )
}
